import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Checkbox,
  FormControl,
  Icon,
  IconSize,
  Radio,
  Select,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { Scalars, WorkPackageSearchByTerm } from '@bechtel/types';
import { FieldArray, Form, Formik } from 'formik';
import { find, findIndex, isEqual, some } from 'lodash';
import React, { ChangeEvent, FC } from 'react';
import {
  DrawerCloseStyled,
  DrawerFooterButtonStyled,
  DrawerSubHeaderStyled,
  DrawerHeaderStyled,
} from '@bechtel/client-common';
import {
  drawerValues,
  DrawerValuesProps,
  filterByList,
  searchByList,
  searchScopeList,
  sortByList,
  SortDirection,
} from '../form-state';
import {
  DrawerBackdropStyled,
  DrawerOuterStyled,
  ListSelectionHeadingStyled,
  DrawerListSelectionStyled,
} from './styled';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const SortSelectedItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {i18n._(item.label)}
    </Text>
  );
};

const SortListItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {i18n._(item.label)}
    </Text>
  );
};
export interface SearchByListProps {
  searchByTerm: WorkPackageSearchByTerm;
  label: Scalars['String'];
  scope: Scalars['Boolean'][];
}
interface DrawerProps {
  close: () => void;
  initialValues: DrawerValuesProps;
  onSubmit: (values: DrawerValuesProps) => void;
}

export const Drawer: FC<DrawerProps> = ({ close, onSubmit, initialValues }) => {
  const { i18n } = useLingui();
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { resetForm }) => {
        resetForm();
        close();
        onSubmit(values);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ setFieldValue, values, isValid, dirty, setValues }) => {
        const isAsc = values.sortDir === SortDirection.ASC;

        return (
          <Form data-testid="drawer-container">
            <DrawerBackdropStyled>
              <DrawerOuterStyled>
                <DrawerHeaderStyled title={t`Filters`}>
                  <DrawerCloseStyled onClick={close} data-testid="drawer-close-button"></DrawerCloseStyled>
                </DrawerHeaderStyled>
                <SideSheetBody>
                  <SideSheetContentCol>
                    <Box px={{ sm: '400' }} py={{ sm: '500' }}>
                      <Box mr={{ sm: '400' }} flexGrow={{ sm: 1 }}>
                        <FormControl
                          style={{ flexGrow: 1 }}
                          label={t`Sort By`}
                          css={`
                            font-family: ${({ theme }) => theme.fontFamilies.graphik};
                          `}
                        >
                          <Select
                            data-testid="sort-by"
                            size={TextFieldSize.LARGE}
                            appearance={TextFieldAppearance.NORMAL}
                            selectedItem={find(sortByList, { value: values.sortBy })}
                            items={sortByList}
                            itemComponent={SortListItem}
                            selectedComponent={SortSelectedItem}
                            onSelectedItemChange={(changes: any) => {
                              setFieldValue('sortBy', changes.selectedItem.value);
                            }}
                          />
                        </FormControl>
                      </Box>
                      <Box>
                        <Button
                          data-testid="sort-dir"
                          appearance={ButtonAppearance.SECONDARY}
                          size={ButtonSize.LARGE}
                          css={`
                            padding-left: ${({ theme }) => theme.spacings[450]};
                            padding-right: ${({ theme }) => theme.spacings[450]};
                          `}
                          onClick={() => {
                            const newValue = isAsc ? SortDirection.DESC : SortDirection.ASC;
                            setFieldValue('sortDir', newValue);
                          }}
                        >
                          <Icon
                            fillColor="bechtel.blue.800"
                            icon={isAsc ? 'sort-amount-down' : 'sort-amount-up'}
                            size={IconSize.MEDIUM}
                          />
                        </Button>
                      </Box>
                    </Box>
                    <DrawerSubHeaderStyled>
                      <Trans>Work Packages</Trans>
                    </DrawerSubHeaderStyled>
                    {searchScopeList.map((scope) => {
                      return (
                        <DrawerListSelectionStyled
                          data-testid={`search-scope-${scope.id}`}
                          key={scope.id}
                          onClick={() => {
                            setFieldValue('searchScope', scope.value);
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Radio checked={values.searchScope === scope.value} readOnly />
                          </Box>
                          <Text color="bechtel.colors.grey.1000" size="400">
                            {i18n._(scope.label)}
                          </Text>
                        </DrawerListSelectionStyled>
                      );
                    })}
                    <DrawerSubHeaderStyled>
                      <Trans>Search By</Trans>
                    </DrawerSubHeaderStyled>

                    <FieldArray name="searchBy">
                      {(arrayHelpers) => {
                        return (
                          <>
                            <ListSelectionHeadingStyled data-testid="searchBy-selectAll">
                              <Box mr={{ sm: '500' }}>
                                <Checkbox
                                  data-testid="checkbox-selectAll"
                                  checked={searchByList.length === arrayHelpers.form.values.searchBy.length}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    e.target.checked
                                      ? setFieldValue(
                                          'searchBy',
                                          searchByList.map((commodityType) => {
                                            return commodityType.searchByTerm;
                                          })
                                        )
                                      : setFieldValue('searchBy', [])
                                  }
                                  readOnly
                                />
                              </Box>
                              <Box
                                flexGrow={{ sm: 1 }}
                                justifyContent={{ sm: 'space-between' }}
                                data-testid="label-selectAll"
                              >
                                <Text color="grey.1000" size="400" weight="600">
                                  <Trans> Select All</Trans>
                                </Text>
                                {arrayHelpers.form.values.searchBy.length > 0 && (
                                  <Text color="grey.1000" size="400" data-testid="selected-length">{`${
                                    arrayHelpers.form.values.searchBy.length
                                  } ${t`Selected`}`}</Text>
                                )}
                              </Box>
                            </ListSelectionHeadingStyled>
                            {searchByList
                              .filter((s) => s.scope.includes(values.searchScope))
                              .map((search) => {
                                return (
                                  <DrawerListSelectionStyled key={`${search.searchByTerm.type}`}>
                                    <Box mr={{ sm: '500' }}>
                                      <Checkbox
                                        data-testid={`search-by-${search.searchByTerm.type}`}
                                        checked={some(values.searchBy, (searchItem) => {
                                          return searchItem.type === search.searchByTerm.type;
                                        })}
                                        onChange={() => {
                                          const selected = findIndex(values.searchBy, (searchItem) => {
                                            return searchItem.type === search.searchByTerm.type;
                                          });
                                          if (selected !== -1) {
                                            arrayHelpers.remove(selected);
                                          } else {
                                            arrayHelpers.push(search.searchByTerm);
                                          }
                                        }}
                                        readOnly
                                      />
                                    </Box>
                                    <Text color="grey.1000" size="400">
                                      {i18n._(search.label)}
                                    </Text>
                                  </DrawerListSelectionStyled>
                                );
                              })}
                          </>
                        );
                      }}
                    </FieldArray>

                    <DrawerSubHeaderStyled>
                      <Trans>Filter By</Trans>
                    </DrawerSubHeaderStyled>
                    {filterByList.map((filter) => {
                      return (
                        <DrawerListSelectionStyled
                          key={filter.id}
                          onClick={() => {
                            setFieldValue('filterBy', filter.value);
                          }}
                        >
                          <Box mr={{ sm: '500' }} data-testid={`filter-by-${filter.id}`}>
                            <Radio checked={values.filterBy === filter.value} readOnly />
                          </Box>
                          <Text color="grey.1000" size="400">
                            {i18n._(filter.label)}
                          </Text>
                        </DrawerListSelectionStyled>
                      );
                    })}
                    <Box p={{ sm: '500' }}>
                      <Button
                        data-testid="reset-button"
                        size={ButtonSize.MEDIUM}
                        css={`
                          color: ${({ theme }) => theme.colors.blue[800]};
                          font-weight: ${({ theme }) => theme.fontWeights[600]};
                        `}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={isEqual(drawerValues, values)}
                        onClick={() => {
                          setValues(drawerValues);
                        }}
                      >
                        <Trans>Reset All Filters</Trans>
                      </Button>
                    </Box>
                  </SideSheetContentCol>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        data-testid="cancel-button"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={close}
                      >
                        <Trans> Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        data-testid="apply-filter-button"
                        type="submit"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={!dirty || !isValid}
                      >
                        <Trans>Apply Filters</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </DrawerOuterStyled>
            </DrawerBackdropStyled>
          </Form>
        );
      }}
    </Formik>
  );
};
